/**
 * Structured copy for a field-link tooltip.
 *
 * WHY A MODEL AND NOT A STRING — the tooltip is expected to grow past one
 * sentence (P.O., 2026-08-20). A flat string forces every future line to be
 * concatenated at a call site, which is how copy drifts between sheets. Callers
 * build the model; exactly one renderer flattens it. Adding a line later is a
 * change here and in `FieldLinkLabelComponent`, and in no sheet.
 */
export interface FieldLinkTooltip {
  /** Always present, one line: `Open "Total revenue" in Analysis of Profit or Loss`. */
  headline: string;
  /** Additional lines rendered beneath the headline. Empty today. */
  details?: string[];
}

/**
 * Flatten a tooltip model to the newline-joined text bound to `[pTooltip]`.
 *
 * Rendering relies on `white-space: pre-line` in the tooltip's styles plus
 * `[escape]="true"`. Escaping is NOT optional: schema labels carry markup — the
 * `(-)` prefixes and the `negativeLabel` pipe's output — so an unescaped tooltip
 * would be an HTML-injection seam for a feature that only needs line breaks.
 *
 * Blank lines are dropped so an empty `details` entry cannot open a gap.
 */
export function formatFieldLinkTooltip(
  tooltip: FieldLinkTooltip | null | undefined
): string | null {
  if (!tooltip) return null;
  return [tooltip.headline, ...(tooltip.details ?? [])]
    .filter((line) => line.trim().length > 0)
    .join('\n');
}
